import {
  DeleteOutlined,
  DownloadOutlined,
  InboxOutlined,
  PaperClipOutlined,
  SwapRightOutlined,
} from '@ant-design/icons';
import {
  App,
  Button,
  DatePicker,
  Drawer,
  Form,
  Input,
  Select,
  Tooltip,
  Upload,
} from 'antd';
import { presignIncidenceRequestAttachment } from 'api/incidence-request.api';
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';
import dayjs, { type Dayjs } from 'dayjs';
import { ApiError } from 'types';
import {
  CreateIncidenceRequestPayload,
  IncidenceRequestAttachment,
} from 'types/incidenceRequests';
import { useEffect, useState } from 'react';
import { IncidenceRequestSubmitConfirmModal } from './IncidenceRequestSubmitConfirmModal';
import { downloadIncidenceRequestAttachment } from './incidenceRequestAttachmentDownload';
import {
  EmployeeIncidenceRequest,
  allowedAttachmentExtensions,
} from './incidenceRequest.shared';
import 'styles/sass/incidence-requests.scss';

type IncidenceTypeOption = {
  label: string;
  value: string;
};

type IncidenceRequestDrawerValues = {
  incidenceType?: string;
  incidenceDates?: [Dayjs, Dayjs];
  description?: string;
};

type UploadableEvidenceFile = UploadFile & {
  uploadedAttachment?: IncidenceRequestAttachment;
};

type IncidenceRequestCreateDrawerProps = {
  open: boolean;
  mobileOnly: boolean;
  requestToEdit?: EmployeeIncidenceRequest | null;
  incidenceTypeOptions: IncidenceTypeOption[];
  onSaveDraft: (
    payload: CreateIncidenceRequestPayload,
    requestToEdit?: EmployeeIncidenceRequest | null
  ) => Promise<void>;
  onSubmit: (
    payload: CreateIncidenceRequestPayload,
    requestToEdit?: EmployeeIncidenceRequest | null
  ) => Promise<void>;
  onClose: () => void;
};

type FormValidationError = {
  errorFields?: unknown[];
};

const hasFormValidationErrors = (
  error: unknown
): error is FormValidationError => {
  return Boolean(
    error &&
    typeof error === 'object' &&
    Array.isArray((error as FormValidationError).errorFields)
  );
};

const createUploadFileFromAttachment = (
  attachment: IncidenceRequestAttachment,
  index: number
): UploadableEvidenceFile => ({
  uid: attachment.s3Key ?? `${attachment.name}-${index}`,
  name: attachment.name,
  status: 'done',
  size: attachment.size,
  type: attachment.mimeType,
  uploadedAttachment: attachment,
});

const getFileExtension = (fileName: string) => {
  const lastDotIndex = fileName.lastIndexOf('.');
  if (lastDotIndex < 0) {
    return '';
  }
  return fileName.slice(lastDotIndex).toLowerCase();
};

export const IncidenceRequestCreateDrawer = ({
  open,
  mobileOnly,
  requestToEdit = null,
  incidenceTypeOptions,
  onSaveDraft,
  onSubmit,
  onClose,
}: IncidenceRequestCreateDrawerProps) => {
  const { message } = App.useApp();
  const [form] = Form.useForm<IncidenceRequestDrawerValues>();
  const [evidenceFiles, setEvidenceFiles] = useState<UploadableEvidenceFile[]>(
    []
  );
  const [submitConfirmModalOpen, setSubmitConfirmModalOpen] = useState(false);
  const [loadingAction, setLoadingAction] = useState<
    'save' | 'submit' | 'upload' | null
  >(null);
  const [downloadingFileUid, setDownloadingFileUid] = useState<string | null>(
    null
  );
  const { Dragger } = Upload;

  useEffect(() => {
    if (!open) {
      return;
    }

    form.resetFields();
    setSubmitConfirmModalOpen(false);
    setLoadingAction(null);
    setDownloadingFileUid(null);
    setEvidenceFiles(
      requestToEdit?.attachments.map(createUploadFileFromAttachment) ?? []
    );

    if (requestToEdit) {
      form.setFieldsValue({
        incidenceType: requestToEdit.incidenceCategoryId,
        incidenceDates: [
          dayjs(requestToEdit.startDate),
          dayjs(requestToEdit.endDate),
        ],
        description: requestToEdit.reason,
      });
    }
  }, [form, open, requestToEdit]);

  const closeDrawer = (forceClose = false) => {
    if (loadingAction && !forceClose) {
      return;
    }

    setSubmitConfirmModalOpen(false);
    onClose();
  };

  const buildPayload = async (): Promise<CreateIncidenceRequestPayload> => {
    const values = await form.validateFields();

    const hasUploadingFiles = evidenceFiles.some(
      (file) => file.status === 'uploading'
    );
    if (hasUploadingFiles) {
      throw new Error('Espera a que terminen de cargarse los adjuntos.');
    }

    const hasFailedFiles = evidenceFiles.some(
      (file) => file.status === 'error'
    );
    if (hasFailedFiles) {
      throw new Error(
        'Corrige o elimina los adjuntos con error antes de continuar.'
      );
    }

    const attachments = evidenceFiles
      .map((file) => file.uploadedAttachment)
      .filter(Boolean) as IncidenceRequestAttachment[];

    return {
      incidenceCategory: values.incidenceType!,
      reason: values.description?.trim() ?? '',
      startDate: values.incidenceDates![0].format('YYYY-MM-DD'),
      endDate: values.incidenceDates![1].format('YYYY-MM-DD'),
      ...(attachments.length > 0 ? { attachments } : {}),
    };
  };

  const removeEvidence = (fileUid: string) => {
    setEvidenceFiles((previousFiles) =>
      previousFiles.filter((file) => file.uid !== fileUid)
    );
  };

  const updateEvidenceFile = (
    fileUid: string,
    nextValues: Partial<UploadableEvidenceFile>
  ) => {
    setEvidenceFiles((previousFiles) =>
      previousFiles.map((file) =>
        file.uid === fileUid ? { ...file, ...nextValues } : file
      )
    );
  };

  const uploadFile = async (file: File, fileUid: string) => {
    const mimeType = file.type || 'application/octet-stream';
    const presigned = await presignIncidenceRequestAttachment({
      fileName: file.name,
      mimeType,
    });

    const uploadResponse = await fetch(presigned.data.uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': mimeType,
      },
      body: file,
    });

    if (!uploadResponse.ok) {
      throw new Error('No fue posible cargar el adjunto');
    }

    updateEvidenceFile(fileUid, {
      status: 'done',
      uploadedAttachment: {
        ...presigned.data.attachment,
        size: file.size,
      },
    });
  };

  const validateAttachment = (file: File) => {
    const extension = getFileExtension(file.name);
    const hasValidExtension = allowedAttachmentExtensions.includes(extension);

    if (!hasValidExtension) {
      message.error(
        'Formato no permitido. Solo puedes subir archivos PDF, JPG, JPEG, PNG o MP4.'
      );
      return Upload.LIST_IGNORE;
    }

    if (evidenceFiles.length >= 10) {
      message.error('Solo puedes adjuntar hasta 10 archivos por solicitud.');
      return Upload.LIST_IGNORE;
    }

    return true;
  };

  const uploadProps: UploadProps = {
    accept: allowedAttachmentExtensions.join(','),
    beforeUpload: async (file) => {
      const isValidFile = validateAttachment(file);
      if (isValidFile !== true) {
        return isValidFile;
      }

      const uploadFileItem: UploadableEvidenceFile = {
        uid: file.uid,
        name: file.name,
        size: file.size,
        type: file.type,
        status: 'uploading',
        originFileObj: file,
      };

      setEvidenceFiles((previousFiles) => [...previousFiles, uploadFileItem]);
      setLoadingAction('upload');

      try {
        await uploadFile(file, file.uid);
      } catch (error) {
        const errorMessage =
          error instanceof ApiError
            ? error.message
            : 'No fue posible cargar el adjunto';
        updateEvidenceFile(file.uid, { status: 'error' });
        message.error(errorMessage);
      } finally {
        setLoadingAction(null);
      }

      return false;
    },
    fileList: evidenceFiles,
    multiple: true,
    onRemove: (file) => {
      removeEvidence(file.uid);
      return true;
    },
    showUploadList: false,
  };

  const retryUpload = async (fileUid: string) => {
    const fileToRetry = evidenceFiles.find((file) => file.uid === fileUid);
    const originFile = fileToRetry?.originFileObj as File | undefined;

    if (!originFile) {
      message.error('No fue posible reenviar el archivo seleccionado.');
      return;
    }

    updateEvidenceFile(fileUid, { status: 'uploading' });
    setLoadingAction('upload');

    try {
      await uploadFile(originFile, fileUid);
    } catch (error) {
      const errorMessage =
        error instanceof ApiError
          ? error.message
          : 'No fue posible cargar el adjunto';
      updateEvidenceFile(fileUid, { status: 'error' });
      message.error(errorMessage);
    } finally {
      setLoadingAction(null);
    }
  };

  const downloadEvidence = async (file: UploadableEvidenceFile) => {
    if (file.originFileObj) {
      const fileUrl = URL.createObjectURL(file.originFileObj);
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(fileUrl);
      return;
    }

    if (!requestToEdit?.id || !file.uploadedAttachment) {
      message.info(
        'El adjunto quedó registrado, pero no puede descargarse aquí.'
      );
      return;
    }

    try {
      setDownloadingFileUid(file.uid);
      await downloadIncidenceRequestAttachment({
        requestId: requestToEdit.id,
        attachment: file.uploadedAttachment,
      });
    } catch (downloadError) {
      message.error(
        downloadError instanceof Error
          ? downloadError.message
          : 'No fue posible descargar el adjunto.'
      );
    } finally {
      setDownloadingFileUid(null);
    }
  };

  const saveRequestAsDraft = async () => {
    try {
      setLoadingAction('save');
      const payload = await buildPayload();
      await onSaveDraft(payload, requestToEdit);
      message.success(
        'Tu solicitud se guardó correctamente y no se envió a revisión.'
      );
      closeDrawer(true);
    } catch (error) {
      if (hasFormValidationErrors(error)) {
        message.warning('Completa los campos obligatorios para guardar.');
        return;
      }

      if (error instanceof ApiError || error instanceof Error) {
        message.error(error.message);
        return;
      }

      message.error('Ocurrió un error al guardar la solicitud.');
    } finally {
      setLoadingAction(null);
    }
  };

  const saveAndSendRequest = async () => {
    try {
      await buildPayload();
      setSubmitConfirmModalOpen(true);
    } catch (error) {
      if (hasFormValidationErrors(error)) {
        message.warning(
          'Completa los campos obligatorios para guardar y enviar la solicitud.'
        );
        return;
      }

      if (error instanceof ApiError || error instanceof Error) {
        message.error(error.message);
        return;
      }

      message.error('Ocurrió un error al preparar la solicitud.');
    }
  };

  const confirmSendRequest = async () => {
    try {
      setLoadingAction('submit');
      const payload = await buildPayload();
      setSubmitConfirmModalOpen(false);
      await onSubmit(payload, requestToEdit);
      message.success(
        'Tu solicitud se guardó y se envió al supervisor para revisión.'
      );
      closeDrawer(true);
    } catch (error) {
      if (hasFormValidationErrors(error)) {
        message.warning(
          'Completa los campos obligatorios para guardar y enviar la solicitud.'
        );
        return;
      }

      if (error instanceof ApiError || error instanceof Error) {
        message.error(error.message);
        return;
      }

      message.error('Ocurrió un error al enviar la solicitud.');
    } finally {
      setLoadingAction(null);
    }
  };

  const drawerFolio = requestToEdit?.folio ?? 'Se asignará al guardar';
  const drawerTitle = requestToEdit
    ? 'Editar solicitud de incidencia'
    : 'Crear solicitud de incidencia';

  return (
    <>
      <Drawer
        title={drawerTitle}
        placement="right"
        width={mobileOnly ? '100%' : 500}
        open={open}
        getContainer={false}
        onClose={() => closeDrawer()}
        rootClassName="drawer-wrapper incidence-request-drawer"
        destroyOnHidden
        data-cy="incidence-request-drawer"
        footer={
          <div className="incidence-request-drawer-footer">
            <Button
              type="link"
              className="drawer-cancel-btn"
              onClick={() => closeDrawer()}
              disabled={Boolean(loadingAction)}
              data-cy="incidence-request-cancel-btn"
            >
              Cancelar
            </Button>
            <Button
              className="drawer-save-btn"
              onClick={saveRequestAsDraft}
              loading={loadingAction === 'save'}
              disabled={
                loadingAction === 'submit' || loadingAction === 'upload'
              }
              data-cy="incidence-request-save-btn"
            >
              Guardar
            </Button>
            <Button
              type="primary"
              onClick={saveAndSendRequest}
              loading={loadingAction === 'submit'}
              disabled={loadingAction === 'save' || loadingAction === 'upload'}
              data-cy="incidence-request-save-send-btn"
            >
              Guardar y enviar
            </Button>
          </div>
        }
      >
        <div className="incidence-request-drawer-content">
          <p className="drawer-folio" data-cy="incidence-request-folio">
            Folio {drawerFolio}
          </p>
          <Form
            layout="vertical"
            form={form}
            className="incidence-request-form"
            data-cy="incidence-request-form"
          >
            <Form.Item
              label={<span>Tipo de incidencia</span>}
              name="incidenceType"
              rules={[
                {
                  required: true,
                  message: 'Selecciona el tipo de incidencia',
                },
              ]}
            >
              <Select
                options={incidenceTypeOptions}
                placeholder="Selecciona tipo de incidencia"
                allowClear
                data-cy="incidence-request-type-select"
              />
            </Form.Item>

            <Form.Item
              label={<span>Fechas de incidencia</span>}
              name="incidenceDates"
              rules={[
                {
                  required: true,
                  message: 'Selecciona el rango de fechas',
                },
              ]}
            >
              <DatePicker.RangePicker
                format="DD/MM/YYYY"
                separator={<SwapRightOutlined />}
                placeholder={['Fecha inicial', 'Fecha final']}
                data-cy="incidence-request-dates"
              />
            </Form.Item>

            <Form.Item
              label="Motivo de la incidencia"
              name="description"
              rules={[
                {
                  required: true,
                  whitespace: true,
                  message: 'Describe el motivo de la incidencia',
                },
              ]}
            >
              <Input.TextArea
                rows={4}
                placeholder="Describe qué ocurrió y cualquier información relevante."
                data-cy="incidence-request-reason"
              />
            </Form.Item>

            <Form.Item label="Evidencias (opcional)">
              <div className="incidence-request-evidence">
                {evidenceFiles.length > 0 && (
                  <div
                    className="evidence-files-list"
                    data-cy="incidence-request-files-list"
                  >
                    {evidenceFiles.map((file) => {
                      const hasUploadError = file.status === 'error';

                      return (
                        <div className="evidence-file-row" key={file.uid}>
                          <div className="evidence-file-info">
                            <PaperClipOutlined className="evidence-file-icon" />
                            <span
                              className="evidence-file-name"
                              title={file.name}
                            >
                              {file.name}
                            </span>
                          </div>

                          <div className="evidence-file-actions">
                            {hasUploadError && (
                              <div className="evidence-file-error-wrap">
                                <span className="evidence-file-error-text">
                                  Error al subir
                                </span>
                                <button
                                  type="button"
                                  className="evidence-file-retry-btn"
                                  onClick={() => void retryUpload(file.uid)}
                                >
                                  Reintentar
                                </button>
                              </div>
                            )}

                            <Tooltip title="Descargar adjunto">
                              <Button
                                htmlType="button"
                                type="text"
                                className="evidence-file-action-btn evidence-file-download-btn"
                                icon={<DownloadOutlined />}
                                loading={downloadingFileUid === file.uid}
                                onClick={() => void downloadEvidence(file)}
                              />
                            </Tooltip>

                            <Tooltip title="Eliminar adjunto">
                              <Button
                                htmlType="button"
                                type="text"
                                className="evidence-file-action-btn evidence-file-delete-btn"
                                icon={<DeleteOutlined />}
                                onClick={() => removeEvidence(file.uid)}
                              />
                            </Tooltip>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <Dragger {...uploadProps} data-cy="incidence-request-upload">
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined className="upload-icon" />
                  </p>
                  <p className="upload-main-text">
                    Haz clic o arrastra un archivo en esta área para subirlo
                  </p>
                  <p className="upload-secondary-text">
                    Recuerda subir archivos únicamente
                    <strong> .png .jpg .jpeg .mp4 .pdf</strong>
                  </p>
                </Dragger>
              </div>
            </Form.Item>
          </Form>
        </div>
      </Drawer>

      <IncidenceRequestSubmitConfirmModal
        open={submitConfirmModalOpen}
        mobileOnly={mobileOnly}
        onClose={() => setSubmitConfirmModalOpen(false)}
        onConfirm={() => void confirmSendRequest()}
      />
    </>
  );
};
